import { useMemo } from 'react';
import _isEmpty from 'lodash/isEmpty';
import {
  getFirstCollisionIndex,
  getResizedWidthByCollisions
} from '../utils';
import {
  DEFAULT_VIEWPORT_WALLSIZE,
  DEFAULT_WALLSIZE,
  BASE_CABINET_HEIGHT,
  DEFAULT_WALL_CABINET_HEIGHT,
  DEFAULT_TOUCH_POINT_WIDTH,
  DEFAULT_TOUCH_POINT_START_COLUMN
} from '../../../../store/cabinetVisualizer/selectors';
import { EMPTY_ARRAY } from '../../../../utils/empty-reference';
import { CABINET_SKU, DIRECTION } from '../../cabinetConstants';

// Touch point cabinet types
const TOUCH_POINT_TYPES = {
  GROUND: ['Base'],
  WALL: ['Wall'],
  GAP: ['Filler']
};

// Touch point configuration constants
const TOUCH_POINT_CONFIG = {
  SPACER_SIZE: 9,
  SPACER_ENABLED: false
};

// Touch point factory helpers
const createTouchPoint = (config) => ({
  top: config.top,
  left: config.left,
  right: config.right || config.left + config.width,
  bottom: config.bottom || config.top + config.height,
  width: config.width,
  height: config.height,
  isStart: config.isStart || false,
  type: config.type,
  direction: config.direction,
  ...(config.additionalProps || {})
});

/**
 * Calculates offset position for touch points based on anchor side
 * @param {string} anchorSide - The anchor side (LEFT or RIGHT)
 * @returns {number} The calculated offset
 */
const calculateTouchPointOffset = (anchorSide) => {
  return anchorSide === DIRECTION.LEFT
    ? 0
    : DEFAULT_VIEWPORT_WALLSIZE.Columns - DEFAULT_TOUCH_POINT_WIDTH;
};

/**
 * Calculates min and max columns for products
 * @param {Array} products - Array of products
 * @returns {Object} Object with minColumn and maxColumn
 */
const getProductColumnRange = (products) => {
  if (products.length === 0) return { minColumn: Infinity, maxColumn: -Infinity };

  const validProducts = products.filter((product) => product.Origin.Width > 0);
  const minColumn = validProducts.reduce((min, product) => Math.min(min, product.Origin.Column), Infinity);
  const maxColumn = validProducts.reduce(
    (max, product) => Math.max(max, product.Origin.Column + product.Origin.Width),
    -Infinity
  );

  return { minColumn, maxColumn };
};

/**
 * Generates default touch points for empty walls
 * Creates touch points at standard positions for both wall and base cabinet areas
 * @param {boolean} disableTouchPoints - Whether to disable touch points entirely
 * @returns {Array<Object>} Array of default touch point objects
 */
export function getTouchPointsForEmpty(disableTouchPoints) {
  if (disableTouchPoints) {
    return EMPTY_ARRAY;
  }
  const maxWidth = DEFAULT_VIEWPORT_WALLSIZE.Columns;
  const offSet = DEFAULT_TOUCH_POINT_START_COLUMN + DEFAULT_TOUCH_POINT_WIDTH;
  const wallTpTop = 0;

  return [
    createTouchPoint({
      top: wallTpTop,
      left: offSet,
      width: DEFAULT_TOUCH_POINT_WIDTH,
      height: DEFAULT_WALL_CABINET_HEIGHT,
      isStart: true,
      type: TOUCH_POINT_TYPES.WALL,
      direction: DIRECTION.LEFT
    }),
    createTouchPoint({
      top: wallTpTop,
      left: maxWidth - DEFAULT_TOUCH_POINT_WIDTH - offSet,
      width: DEFAULT_TOUCH_POINT_WIDTH,
      height: DEFAULT_WALL_CABINET_HEIGHT,
      isStart: true,
      type: TOUCH_POINT_TYPES.WALL,
      direction: DIRECTION.RIGHT
    }),
    createTouchPoint({
      top: DEFAULT_WALLSIZE.Rows - BASE_CABINET_HEIGHT,
      left: offSet,
      width: DEFAULT_TOUCH_POINT_WIDTH,
      height: BASE_CABINET_HEIGHT,
      isStart: true,
      type: TOUCH_POINT_TYPES.GROUND,
      direction: DIRECTION.LEFT
    }),
    createTouchPoint({
      top: DEFAULT_WALLSIZE.Rows - BASE_CABINET_HEIGHT,
      left: maxWidth - DEFAULT_TOUCH_POINT_WIDTH - offSet,
      width: DEFAULT_TOUCH_POINT_WIDTH,
      height: BASE_CABINET_HEIGHT,
      isStart: true,
      type: TOUCH_POINT_TYPES.GROUND,
      direction: DIRECTION.RIGHT
    })
  ];
}

export function addCabinetTouchPoints(cabinetBoundings, cabIndex, touchpoints, wallBounds, type, anchorSide) {
  const curTouchPoints = touchpoints;
  const curCab = cabinetBoundings[cabIndex];
  // const curCabWidth = curCab.right - curCab.left;
  const curCabWidth = DEFAULT_TOUCH_POINT_WIDTH;
  let touchPointHeight = BASE_CABINET_HEIGHT;

  const {
    left: wallLeft,
    right: wallRight,
  } = wallBounds;

  let touchPointTop = curCab.top;

  if (type.includes('Wall')) {
    touchPointTop = wallBounds.height - DEFAULT_WALLSIZE.Rows;
    touchPointHeight = DEFAULT_WALL_CABINET_HEIGHT;
  } else if (type.includes('Base')) {
    touchPointTop = wallBounds.height - BASE_CABINET_HEIGHT;
  }

  let touchLeft = {
    left: curCab.left - curCabWidth,
    width: curCabWidth,
    right: curCab.left,
    top: touchPointTop,
    height: touchPointHeight,
    bottom: touchPointTop + touchPointHeight,
    type,
    direction: DIRECTION.LEFT
  };

  let touchRight = {
    left: curCab.right,
    width: curCabWidth,
    right: curCab.right + curCabWidth,
    top: touchPointTop,
    height: touchPointHeight,
    bottom: touchPointTop + touchPointHeight,
    type,
    direction: DIRECTION.RIGHT
  };

  // Adjust left side
  if (touchLeft.right <= wallLeft) {
    touchLeft.isOutWall = true;
  }

  // Adjust right side
  if (touchRight.left >= wallRight) {
    touchRight.isOutWall = true;
  }

  // Verify new touch points aren't colliding with cabinets
  // Check if touch point needs to be resized to prevent colliding with cabient
  touchLeft = getResizedWidthByCollisions(touchLeft, cabinetBoundings);
  touchRight = getResizedWidthByCollisions(touchRight, cabinetBoundings);

  let isTouchLeftAble;
  let isTouchRightAble;

  if (anchorSide) {
    isTouchLeftAble = anchorSide === touchRight.direction && touchLeft.width > 0;
    isTouchRightAble = anchorSide === touchLeft.direction && touchRight.width > 0;
  } else if (curCab) {
    isTouchLeftAble = curCab.touchPointDirection === touchRight.direction;
    isTouchRightAble = curCab.touchPointDirection === touchLeft.direction;
  }

  if (touchLeft.width < TOUCH_POINT_CONFIG.SPACER_SIZE) {
    touchLeft.isSpacer = true;
    touchLeft.type = TOUCH_POINT_TYPES.GAP;

    if (!TOUCH_POINT_CONFIG.SPACER_ENABLED) {
      isTouchLeftAble = false;
    }
  }

  if (touchRight.width < TOUCH_POINT_CONFIG.SPACER_SIZE) {
    touchRight.isSpacer = true;
    touchRight.type = TOUCH_POINT_TYPES.GAP;

    if (!TOUCH_POINT_CONFIG.SPACER_ENABLED) {
      isTouchRightAble = false;
    }
  }

  // Add touch point if there is no collision otherwise replace with the most recent touch point
  function addCurrentTouchPoint(newTouchPoint) {
    const foundCollision = getFirstCollisionIndex(newTouchPoint, curTouchPoints);
    if (foundCollision === -1) {
      curTouchPoints.push(newTouchPoint);
    } else if (newTouchPoint.width >= curTouchPoints[foundCollision].width) { // Keep the biggest touch point
      curTouchPoints[foundCollision] = newTouchPoint;
    }
  }

  // Add left touch point
  if (isTouchLeftAble) {
    addCurrentTouchPoint(touchLeft);
  }

  // Add right touch point
  if (isTouchRightAble) {
    addCurrentTouchPoint(touchRight);
  }
}

/**
 * Generates touch points for wall cabinets
 * @param {Array} topProducts - Array of top row products (wall cabinets)
 * @param {Array} defaultTouchPoints - Default touch points for empty walls
 * @param {string} anchorSide - The anchor side (LEFT or RIGHT)
 * @returns {Array<Object>} Array of wall touch points
 */
export const getTouchPointForWall = (topProducts, defaultTouchPoints, anchorSide) => {
  const offset = calculateTouchPointOffset(anchorSide);

  const defaultTouchPointsForWall = defaultTouchPoints
    .filter((tp) => tp.type === TOUCH_POINT_TYPES.WALL)
    .map((tp) => ({ ...tp, left: offset, right: tp.right - offset }));

  if (topProducts.length === 0) {
    return defaultTouchPointsForWall.filter((tp) => tp.direction === anchorSide);
  }

  const { minColumn, maxColumn } = getProductColumnRange(topProducts);

  if (topProducts.length > 0 && defaultTouchPointsForWall.length > 0) {
    const theTouchPoint = defaultTouchPointsForWall.find((tp) => tp.direction !== anchorSide);
    theTouchPoint.left = anchorSide === DIRECTION.RIGHT ? minColumn - DEFAULT_TOUCH_POINT_WIDTH : maxColumn;

    const anchorTouchPoint = defaultTouchPointsForWall.find((tp) => tp.direction === anchorSide);
    anchorTouchPoint.left = offset;

    return [theTouchPoint, anchorTouchPoint];
  }

  return defaultTouchPointsForWall;
};

/**
 * Generates touch points for base cabinets
 * @param {Array} bottomProducts - Array of bottom row products (base cabinets)
 * @param {Array} defaultTouchPoints - Default touch points for empty walls
 * @param {string} anchorSide - The anchor side (LEFT or RIGHT)
 * @returns {Array<Object>} Array of base touch points
 */
export const getTouchPointForBase = (bottomProducts, defaultTouchPoints, anchorSide) => {
  const offset = calculateTouchPointOffset(anchorSide);
  const defaultTouchPointsForBase = defaultTouchPoints
    .filter((tp) => tp.type === TOUCH_POINT_TYPES.GROUND)
    .map((tp) => ({ ...tp, left: offset, right: tp.right - offset }));

  const hasProducts = bottomProducts.some((product) => product.Sku !== CABINET_SKU.VOID_SPACE);
  if (bottomProducts.length === 0 || !hasProducts) {
    return defaultTouchPointsForBase.filter((tp) => tp.direction === anchorSide);
  }

  const { minColumn, maxColumn } = getProductColumnRange(bottomProducts);

  if (bottomProducts.length > 0 && defaultTouchPointsForBase.length > 0) {
    const theTouchPoint = defaultTouchPointsForBase.find((tp) => tp.direction !== anchorSide);
    theTouchPoint.left = anchorSide === DIRECTION.RIGHT ? minColumn - DEFAULT_TOUCH_POINT_WIDTH : maxColumn;

    const anchorTouchPoint = defaultTouchPointsForBase.find((tp) => tp.direction === anchorSide);
    anchorTouchPoint.left = offset;

    return [theTouchPoint, anchorTouchPoint];
  }

  return defaultTouchPointsForBase;
};

/**
 * Main hook for managing touch points in cabinet visualization
 * @param {Array} cabinets - Array of cabinet products
 * @param {Array} topProducts - Array of top row products (wall cabinets)
 * @param {Array} bottomProducts - Array of bottom row products (base cabinets)
 * @param {Object} options - Configuration options
 * @param {string} options.anchorSide - The anchor side (LEFT or RIGHT)
 * @param {boolean} options.disableTouchPoints - Whether to disable touch points entirely
 * @param {boolean} enableTouchPointsOnBothSides - Enable touch points on both sides (legacy param)
 * @returns {Array<Object>} Array of touch point objects with position and type information
 */
export const useTouchPointManager = (
  cabinets,
  topProducts,
  bottomProducts,
  options = {},
  enableTouchPointsOnBothSides
) => {
  const { anchorSide, disableTouchPoints } = options;

  return useMemo(() => {
    // Early return if touch points are disabled or no cabinets exist
    if (disableTouchPoints || _isEmpty(cabinets)) {
      return getTouchPointsForEmpty(disableTouchPoints);
    }

    // Ensure we have valid arrays to work with
    const wallProducts = topProducts || EMPTY_ARRAY;
    const baseProducts = bottomProducts || EMPTY_ARRAY;

    // Generate default touch points for empty walls
    const defaultTouchPoints = getTouchPointsForEmpty(false);

    // Generate touch points for wall and base cabinets
    const wallTouchPoints = getTouchPointForWall(wallProducts, defaultTouchPoints, anchorSide);
    const baseTouchPoints = getTouchPointForBase(baseProducts, defaultTouchPoints, anchorSide);

    // Combine all touch points
    return [...wallTouchPoints, ...baseTouchPoints];
  }, [cabinets, topProducts, bottomProducts, anchorSide, disableTouchPoints, enableTouchPointsOnBothSides]);
};

export default useTouchPointManager;
